"use client"

import * as React from "react"
import { Loader2 } from "lucide-react"

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { buttonVariants } from "@/components/ui/button"
import { cn } from "@/lib/utils"

interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: React.ReactNode
  /** Label for the confirm button. Defaults to "Delete". */
  confirmLabel?: string
  /** Label for the cancel button. Defaults to "Cancel". */
  cancelLabel?: string
  /** Destructive (red) confirm button. Defaults to true — most confirms are deletes. */
  destructive?: boolean
  /**
   * Called when the user confirms. May be async — the dialog shows a spinner,
   * disables both buttons while it runs, and closes when it resolves. If it
   * throws, the dialog stays open so the user can retry or cancel.
   */
  onConfirm: () => void | Promise<void>
}

/**
 * Themed replacement for `window.confirm()` on destructive actions.
 *
 * Usage — hold the pending target in state and render one dialog:
 *
 *   const [pendingDelete, setPendingDelete] = useState<Item | null>(null)
 *   ...
 *   <Button onClick={() => setPendingDelete(item)}>Delete</Button>
 *   <ConfirmDialog
 *     open={!!pendingDelete}
 *     onOpenChange={(open) => { if (!open) setPendingDelete(null) }}
 *     title="Delete item?"
 *     description={`This will permanently delete "${pendingDelete?.name}".`}
 *     onConfirm={() => handleDelete(pendingDelete!)}
 *   />
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Delete",
  cancelLabel = "Cancel",
  destructive = true,
  onConfirm,
}: ConfirmDialogProps) {
  const [pending, setPending] = React.useState(false)

  const handleConfirm = async (e: React.MouseEvent) => {
    // Radix's Action closes the dialog on click; we close manually after
    // onConfirm settles so async work can show its pending state.
    e.preventDefault()
    setPending(true)
    try {
      await onConfirm()
      onOpenChange(false)
    } catch {
      // Caller surfaces its own error toast; keep the dialog open for retry.
    } finally {
      setPending(false)
    }
  }

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (pending) return // don't allow dismissing mid-action
        onOpenChange(next)
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>{cancelLabel}</AlertDialogCancel>
          {/* Plain button (not AlertDialogAction) so the close is fully manual */}
          <button
            type="button"
            className={cn(
              buttonVariants({ variant: destructive ? "destructive" : "default" })
            )}
            disabled={pending}
            onClick={handleConfirm}
          >
            {pending && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmLabel}
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

// ── Promise-style confirm ─────────────────────────────────────────────────────

export interface ConfirmOptions {
  title: string
  description?: React.ReactNode
  confirmLabel?: string
  cancelLabel?: string
  destructive?: boolean
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>

const ConfirmContext = React.createContext<ConfirmFn | null>(null)

/**
 * `const confirm = useConfirm(); if (!(await confirm({ title }))) return`
 *
 * The themed drop-in for `window.confirm()` where the caller needs a yes/no
 * answer inline (inside a hook, midway through a handler) rather than holding
 * a pending target in state. The browser's own confirm box is unstyled, ignores
 * dark mode and the palette, and blocks the whole tab.
 *
 * Outside a ConfirmProvider (an isolated unit test, say) it falls back to the
 * native box, so a missing provider degrades to the old behaviour rather than
 * silently answering "yes" to a delete.
 */
export function useConfirm(): ConfirmFn {
  const ctx = React.useContext(ConfirmContext)
  return ctx ?? ((o: ConfirmOptions) =>
    Promise.resolve(window.confirm(typeof o.description === 'string' ? `${o.title}\n\n${o.description}` : o.title)))
}

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [request, setRequest] = React.useState<
    (ConfirmOptions & { resolve: (ok: boolean) => void }) | null
  >(null)

  const confirm = React.useCallback<ConfirmFn>(
    (options) => new Promise<boolean>((resolve) => setRequest({ ...options, resolve })),
    []
  )

  const settle = (ok: boolean) => {
    request?.resolve(ok)
    setRequest(null)
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={!!request} onOpenChange={(open) => { if (!open) settle(false) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{request?.title}</AlertDialogTitle>
            {request?.description && (
              <AlertDialogDescription>{request.description}</AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{request?.cancelLabel ?? 'Cancel'}</AlertDialogCancel>
            <button
              type="button"
              className={cn(buttonVariants({ variant: request?.destructive === false ? 'default' : 'destructive' }))}
              onClick={() => settle(true)}
            >
              {request?.confirmLabel ?? 'Delete'}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  )
}
